import { useDebounce } from '@/hooks/useDebounce';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session, CreateSessionPayload, ContentBlock } from '@/store/api/sessionsApi';
import { ContentBlocksEditor } from './ContentBlocksEditor';
import { useUploadFileMutation } from '@/store/api/storageApi';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { Badge } from '@/components/ui/badge';
import { AdminSessionPreviewModal } from './AdminSessionPreviewModal';
import {
  Loader2,
  Plus,
  Trash2,
  Link2,
  FileText,
  BookOpen,
  Mic2,
  Clock,
  Calendar,
  Tag,
  AlignLeft,
  Video,
  Info,
  CheckSquare,
  Image,
  Upload,
  Globe,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronsUpDown,
  AlertTriangle,
} from 'lucide-react';

const getUrlWarning = (url: string) => {
  if (!url) return null;
  if (url.startsWith('/')) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('prnt.sc'))
      return 'Lightshot (prnt.sc) URLs are webpages, not images. Right-click the image and select "Copy image address".';
    if (u.hostname.includes('drive.google.com'))
      return 'Google Drive links cannot be embedded directly as images.';
    if (u.hostname.includes('dropbox.com') && !url.includes('raw=1'))
      return 'Dropbox links must end with ?raw=1 to work as images.';
    const ext = u.pathname.split('.').pop()?.toLowerCase() || '';
    if (['html', 'php', 'aspx'].includes(ext))
      return 'This looks like a webpage link, not a direct image file.';
    return null;
  } catch {
    return null;
  }
};

interface SessionFormProps {
  initialData?: Session;
  onSubmit: (data: CreateSessionPayload) => Promise<void>;
  onAutoSave?: (data: CreateSessionPayload) => Promise<void>;
  isSubmitting: boolean;
  submitLabel: string;
}

// ─── Image Preview Modal ──────────────────────────────────────────────────────
function ImagePreviewModal({
  images,
  startIdx,
  onClose,
}: {
  images: { url: string; caption?: string }[];
  startIdx: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(startIdx);
  const total = images.length;
  const img = images[idx];

  // Keyboard nav
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + total) % total);
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % total);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center !m-0"
      style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center transition-all"
        aria-label="Close preview"
      >
        <X size={20} />
      </button>

      {/* Counter */}
      {total > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-black/50 text-white text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm">
          {idx + 1} / {total}
        </div>
      )}

      {/* Prev */}
      {total > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIdx((i) => (i - 1 + total) % total);
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full w-12 h-12 flex items-center justify-center transition-all"
          aria-label="Previous"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* Next */}
      {total > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIdx((i) => (i + 1) % total);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full w-12 h-12 flex items-center justify-center transition-all"
          aria-label="Next"
        >
          <ChevronRight size={24} />
        </button>
      )}

      {/* Image */}
      <div
        className="relative max-w-5xl w-full mx-16 flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {img && (
          <>
            <img
              key={img.url}
              src={img.url}
              alt={img.caption || `Image ${idx + 1}`}
              className="max-h-[78vh] w-full object-contain rounded-2xl shadow-2xl"
              style={{ background: '#111' }}
            />
            {img.caption && (
              <div className="bg-black/60 backdrop-blur-sm text-white text-sm px-5 py-2.5 rounded-xl max-w-lg text-center">
                {img.caption}
              </div>
            )}
            {/* Dot nav */}
            {total > 1 && (
              <div className="flex gap-1.5">
                {images.map((_, di) => (
                  <button
                    key={di}
                    onClick={() => setIdx(di)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      di === idx ? 'bg-white scale-110' : 'bg-white/35 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-foreground mb-1.5">
        {label} {required && <span className="text-destructive">*</span>}
        {hint && (
          <span className="text-muted-foreground font-normal text-xs ml-1.5 text-muted-foreground/80">
            — {hint}
          </span>
        )}
      </label>
      {children}
      {error && (
        <p className="text-[13px] font-medium text-destructive mt-1.5 flex items-center gap-1">
          ⚠ {error}
        </p>
      )}
    </div>
  );
}

// Styled input with error state
const inp = (err?: string) =>
  `w-full border rounded-lg px-3.5 py-2 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm ${
    err
      ? 'border-destructive bg-destructive/5'
      : 'border-input bg-background hover:border-primary/50'
  }`;

// ─── Date helpers (module scope so they are hoisted) ─────────────────────────
function parseDatetime(dateStr: string): { date: string; time: string } {
  if (!dateStr) return { date: '', time: '' };
  const cleaned = dateStr.replace(/\s*[·\.]\.?\s*/g, ', ');
  const d = new Date(cleaned);
  if (!isNaN(d.getTime())) {
    const hasTime = /\d:\d{2}/.test(dateStr);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return { date: `${yyyy}-${mm}-${dd}`, time: hasTime ? `${hh}:${min}` : '' };
  }
  return { date: '', time: '' };
}

function buildDateDisplay(date: string, time: string): string {
  if (!date) return '';
  const d = new Date(date + 'T00:00:00');
  if (isNaN(d.getTime())) return date;
  const formatted = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  if (!time) return formatted;
  const [h = 0, m = 0] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 || 12;
  return `${formatted} \u00b7 ${hh}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function SessionForm({
  initialData,
  onSubmit,
  onAutoSave,
  isSubmitting,
  submitLabel,
}: SessionFormProps) {
  const navigate = useNavigate();
  const [uploadFile] = useUploadFileMutation();

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: true,
    speaker: true,
    media: true,
    content: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const collapseAllSections = () => {
    setExpandedSections({ basic: false, speaker: false, media: false, content: false });
  };

  const expandAllSections = () => {
    setExpandedSections({ basic: true, speaker: true, media: true, content: true });
  };

    const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error' | 'paused'>(
    'idle'
  );
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [lastSavedForm, setLastSavedForm] = useState<CreateSessionPayload | null>(
    initialData
      ? {
          slug: initialData.slug,
          title: initialData.title,
          tag: initialData.tag,
          description: initialData.description,
          speaker: initialData.speaker,
          speakerRole: initialData.speakerRole,
          duration: initialData.duration,
          date: initialData.date,
          youtubeUrl: initialData.youtubeUrl || '',
          thumbnailUrl: initialData.thumbnailUrl || '',
          speakerImageUrl: initialData.speakerImageUrl || '',
          contentBlocks: initialData.contentBlocks || [],
          status: initialData.status as 'Draft' | 'Published',
        }
      : null
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Thumbnail upload mode & ref
  const [thumbnailMode, setThumbnailMode] = useState<'url' | 'file'>('url');
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailUploadError, setThumbnailUploadError] = useState('');
  const [thumbnailPreview, setThumbnailPreview] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement | null>(null);

  // Speaker Image upload mode & ref
  const [speakerImageMode, setSpeakerImageMode] = useState<'url' | 'file'>('url');
  const [speakerImageUploading, setSpeakerImageUploading] = useState(false);
  const [speakerImageUploadError, setSpeakerImageUploadError] = useState('');
  const [speakerImagePreview, setSpeakerImagePreview] = useState(false);
  const speakerImageInputRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<CreateSessionPayload>({
    slug: '',
    title: '',
    tag: '',
    description: '',
    speaker: '',
    speakerRole: '',
    duration: '',
    date: '',
    youtubeUrl: '',
    thumbnailUrl: '',
    speakerImageUrl: '',
    contentBlocks: [],
    status: 'Draft',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const lastLoadedSlug = useRef<string | null>(null);

  useEffect(() => {
    if (initialData && lastLoadedSlug.current !== initialData.slug) {
      lastLoadedSlug.current = initialData.slug;
      
      const newFormState = {
        slug: initialData.slug,
        title: initialData.title,
        tag: initialData.tag,
        description: initialData.description,
        speaker: initialData.speaker,
        speakerRole: initialData.speakerRole,
        duration: initialData.duration,
        date: initialData.date,
        youtubeUrl: initialData.youtubeUrl || '',
        thumbnailUrl: initialData.thumbnailUrl || '',
        speakerImageUrl: initialData.speakerImageUrl || '',
        contentBlocks: initialData.contentBlocks || [],
        status: initialData.status,
      };
      
      setForm(newFormState);
      setLastSavedForm(newFormState);
      // Re-sync date split fields when initialData changes
      const pdt = parseDatetime(initialData.date || '');
      setDateInput(pdt.date);
      setTimeInput(pdt.time);
    }
  }, [initialData]);

  // Silent Validation for AutoSave
  const isFormValidSilent = (f: CreateSessionPayload): boolean => {
    if (
      !f.slug.trim() ||
      !f.title.trim() ||
      !f.tag.trim() ||
      !f.description.trim() ||
      !f.speaker.trim() ||
      !f.speakerRole.trim() ||
      !f.duration.trim() ||
      !f.date.trim()
    ) {
      return false;
    }
    return true;
  };

  const debouncedForm = useDebounce(form, 2000); // 2 seconds debounce

  useEffect(() => {
    if (!onAutoSave) return;

    // Do not autosave if published to prevent accidental live overwrites
    if (debouncedForm.status === 'Published') {
      if (autoSaveStatus !== 'idle') setAutoSaveStatus('idle');
      return;
    }

    // Check if form has changed from last saved state
    const formString = JSON.stringify(debouncedForm);
    const lastSavedString = lastSavedForm ? JSON.stringify(lastSavedForm) : null;

    if (formString === lastSavedString) {
      // Don't revert status to idle if it was 'saved', we want to keep the "Last saved at" message
      return;
    }

    if (!isFormValidSilent(debouncedForm)) {
      setAutoSaveStatus('paused');
      return;
    }

    const saveForm = async () => {
      setAutoSaveStatus('saving');
      try {
        const payload: CreateSessionPayload = {
          ...debouncedForm,
          youtubeUrl: debouncedForm.youtubeUrl || null,
          thumbnailUrl: debouncedForm.thumbnailUrl || null,
          speakerImageUrl: debouncedForm.speakerImageUrl || null,
        };
        await onAutoSave(payload);
        setLastSavedForm(debouncedForm);
        setLastSavedAt(new Date());
        setAutoSaveStatus('saved');
      } catch (e) {
        setAutoSaveStatus('error');
      }
    };
    
    saveForm();
  }, [debouncedForm, onAutoSave, lastSavedForm]);

  const set = (key: keyof CreateSessionPayload, value: any) => {
    setForm((prev) => {
      let newStatus = prev.status;
      if (prev.status === 'Published' && key !== 'status') {
        newStatus = 'Draft';
      } else if (key === 'status') {
        newStatus = value;
      }
      return { ...prev, [key]: value, status: newStatus };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.slug.trim()) e.slug = 'Required';
    else if (!/^[a-z0-9-]+$/.test(form.slug))
      e.slug = 'Only lowercase letters, numbers and hyphens';
    if (!form.title.trim()) e.title = 'Required';
    if (!form.tag.trim()) e.tag = 'Required';
    if (!form.description.trim()) e.description = 'Required';
    if (!form.speaker.trim()) e.speaker = 'Required';
    if (!form.speakerRole.trim()) e.speakerRole = 'Required';
    if (!form.duration.trim()) {
      e.duration = 'Required';
    } else if (!/^(\d+(\.\d+)?)\s*(mins?|hrs?|hours?|minutes?)$/i.test(form.duration.trim())) {
      e.duration = 'Use format like "45 mins" or "1.5 hrs"';
    }
    if (!form.date.trim()) e.date = 'Required';

    // Validate Resources block
    form.contentBlocks?.forEach((block) => {
      if (block.type === 'resources') {
        const items = block.data?.items || [];
        items.forEach((item: any) => {
          if (item.url && !item.label?.trim()) {
            e.contentBlocks = 'A label is required for all resource links.';
          }
        });
      }
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateField = (field: keyof CreateSessionPayload | 'time') => {
    let error = '';
    const f = form;
    switch (field) {
      case 'title':
        if (!f.title.trim()) error = 'Required';
        break;
      case 'slug':
        if (!f.slug.trim()) error = 'Required';
        else if (!/^[a-z0-9-]+$/.test(f.slug)) error = 'Only lowercase letters, numbers and hyphens';
        break;
      case 'tag':
        if (!f.tag.trim()) error = 'Required';
        break;
      case 'description':
        if (!f.description.trim()) error = 'Required';
        break;
      case 'speaker':
        if (!f.speaker.trim()) error = 'Required';
        break;
      case 'speakerRole':
        if (!f.speakerRole.trim()) error = 'Required';
        break;
      case 'duration':
        if (!f.duration.trim()) error = 'Required';
        else if (!/^(\d+(\.\d+)?)\s*(mins?|hrs?|hours?|minutes?)$/i.test(f.duration.trim())) error = 'Use format like "45 mins" or "1.5 hrs"';
        break;
      case 'date':
      case 'time':
        if (!dateInput.trim() || !timeInput.trim()) error = 'Required';
        break;
    }

    const fieldKey = field === 'time' ? 'date' : field;
    
    setErrors((prev) => {
      const next = { ...prev };
      if (error) next[fieldKey] = error;
      else delete next[fieldKey];
      return next;
    });
  };

  const handleSubmit = async (status: 'Draft' | 'Published') => {
    if (!validate()) return;
    const formWithStatus = { ...form, status };
    // Temporarily update form status for validation
    setForm((prev) => ({ ...prev, status }));
    const cleanedContentBlocks = formWithStatus.contentBlocks?.map((block) => {
      if ((block.type === 'learning-points' || block.type === 'learning_points') && Array.isArray(block.data?.points)) {
        return {
          ...block,
          data: {
            ...block.data,
            points: block.data.points.filter((p: any) => typeof p === 'string' && p.trim() !== ''),
          },
        };
      }
      return block;
    });

    const payload: CreateSessionPayload = {
      ...formWithStatus,
      contentBlocks: cleanedContentBlocks,
      youtubeUrl: formWithStatus.youtubeUrl || null,
      thumbnailUrl: formWithStatus.thumbnailUrl || null,
      speakerImageUrl: formWithStatus.speakerImageUrl || null,
    };
    await onSubmit(payload);
  };

  // ── Date + Time state (initialised with module-level parseDatetime)
  const [dateInput, setDateInput] = useState(() => parseDatetime(initialData?.date || '').date);
  const [timeInput, setTimeInput] = useState(() => parseDatetime(initialData?.date || '').time);

  const handleDateChange = (date: string, time: string) => {
    setDateInput(date);
    setTimeInput(time);
    set('date', buildDateDisplay(date, time));
  };

  // ── Slug helpers ─────────────────────────────────────────────────────────
  const slugify = (v: string) =>
    v
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);

  const handleTitleChange = (val: string) => {
    set('title', val);
    if (!initialData) set('slug', slugify(val));
  };

  // Thumbnail file upload handler
  const handleThumbnailFileChange = async (file: File | null) => {
    if (!file) return;
    setThumbnailUploading(true);
    setThumbnailUploadError('');
    try {
      const result = await uploadFile(file).unwrap();
      set('thumbnailUrl', result.url);
    } catch (err: any) {
      setThumbnailUploadError(err?.data?.message || err?.message || 'Upload failed');
    } finally {
      setThumbnailUploading(false);
    }
  };

  // Speaker Image file upload handler
  const handleSpeakerImageFileChange = async (file: File | null) => {
    if (!file) return;
    setSpeakerImageUploading(true);
    setSpeakerImageUploadError('');
    try {
      const result = await uploadFile(file).unwrap();
      set('speakerImageUrl', result.url);
    } catch (err: any) {
      setSpeakerImageUploadError(err?.data?.message || err?.message || 'Upload failed');
    } finally {
      setSpeakerImageUploading(false);
    }
  };

  const renderSectionHeader = (id: string, icon: React.ReactNode, text: string) => (
    <button
      type="button"
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/30 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg text-primary shadow-sm">{icon}</div>
        <h3 className="font-semibold text-base text-foreground tracking-tight">{text}</h3>
      </div>
      <div
        className={`p-1 rounded-md text-muted-foreground hover:text-foreground transition-transform duration-200 ${
          expandedSections[id] ? 'rotate-180' : ''
        }`}
      >
        <ChevronDown size={18} />
      </div>
    </button>
  );

  return (
    <>
      {/* Thumbnail preview modal */}
      {thumbnailPreview && form.thumbnailUrl && (
        <ImagePreviewModal
          images={[{ url: form.thumbnailUrl, caption: 'Session Thumbnail' }]}
          startIdx={0}
          onClose={() => setThumbnailPreview(false)}
        />
      )}

      {/* Speaker Image preview modal */}
      {speakerImagePreview && form.speakerImageUrl && (
        <ImagePreviewModal
          images={[{ url: form.speakerImageUrl, caption: 'Speaker Image' }]}
          startIdx={0}
          onClose={() => setSpeakerImagePreview(false)}
        />
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6 items-start">
        {/* ── LEFT COLUMN — All fields ── */}
        <div className="space-y-4">
          <div className="flex items-center justify-end gap-2 -mb-2">
             <Button type="button" variant="ghost" size="sm" onClick={collapseAllSections} className="text-xs text-muted-foreground hover:text-foreground h-7 px-2">
               Collapse All
             </Button>
             <Button type="button" variant="ghost" size="sm" onClick={expandAllSections} className="text-xs text-muted-foreground hover:text-foreground h-7 px-2">
               Expand All
             </Button>
          </div>

          {/* 1. Basic Info */}
          <div className="bg-card border border-border shadow-sm rounded-xl overflow-hidden transition-all duration-200">
            {renderSectionHeader('basic', <Info size={18} />, 'Basic Information')}

            {expandedSections['basic'] && (
              <div className="px-5 pb-5 pt-3 space-y-4 border-t border-border/40 animate-in slide-in-from-top-2 fade-in duration-200">

            <Field label="Session Title" required error={errors.title}>
              <input
                className={inp(errors.title)}
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                onBlur={() => validateField('title')}
                placeholder="e.g. Decentralized Identity & Verifiable Credentials"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Slug" hint="URL identifier" required error={errors.slug}>
                <input
                  className={inp(errors.slug)}
                  value={form.slug}
                  onChange={(e) => set('slug', slugify(e.target.value))}
                  onBlur={() => validateField('slug')}
                  placeholder="decentralized-identity"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  🔗 /sessions/
                  <span className="font-medium text-foreground">{form.slug || '...'}</span>
                </p>
              </Field>

              <Field label="Category Tag" required error={errors.tag}>
                <input
                  className={inp(errors.tag)}
                  value={form.tag}
                  onChange={(e) => set('tag', e.target.value.toUpperCase())}
                  onBlur={() => validateField('tag')}
                  placeholder="CRYPTOGRAPHY"
                />
              </Field>
            </div>

            <Field
              label="Short Description"
              hint="shown on the sessions listing card"
              required
              error={errors.description}
            >
              <RichTextEditor
                value={form.description}
                onChange={(val) => set('description', val)}
                placeholder="One sentence describing what this session is about"
                minHeight={100}
              />
            </Field>
              </div>
            )}
          </div>

          {/* 2. Speaker */}
          <div className="bg-card border border-border shadow-sm rounded-xl overflow-hidden transition-all duration-200">
            {renderSectionHeader('speaker', <Mic2 size={18} />, 'Speaker & Schedule')}
            
            {expandedSections['speaker'] && (
              <div className="px-5 pb-5 pt-3 space-y-4 border-t border-border/40 animate-in slide-in-from-top-2 fade-in duration-200">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Speaker Name" required error={errors.speaker}>
                <input
                  className={inp(errors.speaker)}
                  value={form.speaker}
                  onChange={(e) => set('speaker', e.target.value)}
                  onBlur={() => validateField('speaker')}
                  placeholder="e.g. Kamlesh Nagwarne"
                />
              </Field>
              <Field label="Speaker Role / Title" required error={errors.speakerRole}>
                <input
                  className={inp(errors.speakerRole)}
                  value={form.speakerRole}
                  onChange={(e) => set('speakerRole', e.target.value)}
                  onBlur={() => validateField('speakerRole')}
                  placeholder="e.g. Cryptography & Secure Systems Mentor"
                />
              </Field>
              <Field label="Duration" hint="e.g. 45 mins, 2 hrs" required error={errors.duration}>
                <input
                  className={inp(errors.duration)}
                  value={form.duration}
                  onChange={(e) => set('duration', e.target.value)}
                  onBlur={() => validateField('duration')}
                  placeholder="45 mins"
                />
              </Field>
              <Field label="Session Date & Time" required error={errors.date}>
                <div className="flex gap-3">
                  <input
                    type="date"
                    className={inp(errors.date) + ' flex-1'}
                    value={dateInput}
                    onChange={(e) => handleDateChange(e.target.value, timeInput)}
                    onBlur={() => validateField('date')}
                  />
                  <input
                    type="time"
                    className={inp(errors.date) + ' flex-1'}
                    value={timeInput}
                    onChange={(e) => handleDateChange(dateInput, e.target.value)}
                    onBlur={() => validateField('time')}
                  />
                </div>
                {form.date && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    📅 Stored as: <span className="font-medium text-foreground">{form.date}</span>
                  </p>
                )}
              </Field>
            </div>

            {/* Speaker Image — Upload / URL toggle */}
            <div className="mt-4 border-t border-border/60 pt-4">
              <Field label="Speaker Image" hint="optional, initials used if omitted">
                <div className="space-y-3">
                  <div className="flex items-center bg-muted rounded-lg p-0.5 w-fit text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        setSpeakerImageMode('file');
                        setSpeakerImageUploadError('');
                      }}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all ${
                        speakerImageMode === 'file'
                          ? 'bg-background text-foreground shadow-sm font-semibold'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Upload size={11} /> Upload File
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSpeakerImageMode('url');
                        setSpeakerImageUploadError('');
                      }}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all ${
                        speakerImageMode === 'url'
                          ? 'bg-background text-foreground shadow-sm font-semibold'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Globe size={11} /> Enter URL
                    </button>
                  </div>

                  <input
                    ref={speakerImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleSpeakerImageFileChange(e.target.files?.[0] ?? null)}
                  />

                  {speakerImageMode === 'file' && (
                    <div>
                      {!form.speakerImageUrl ? (
                        <button
                          type="button"
                          disabled={speakerImageUploading}
                          onClick={() => speakerImageInputRef.current?.click()}
                          className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 rounded-xl py-6 text-muted-foreground hover:text-primary transition-all disabled:opacity-60"
                        >
                          {speakerImageUploading ? (
                            <>
                              <Loader2 size={22} className="animate-spin" />
                              <span className="text-xs">Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload size={22} />
                              <span className="text-sm font-medium">
                                Click to choose speaker image
                              </span>
                              <span className="text-xs">PNG, JPG, WEBP, GIF</span>
                            </>
                          )}
                        </button>
                      ) : (
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            onClick={() => setSpeakerImagePreview(true)}
                            className="relative group shrink-0"
                            title="Click to preview"
                          >
                            <img
                              src={form.speakerImageUrl}
                              alt="Speaker"
                              className="h-20 w-20 rounded-full object-cover border border-border"
                            />
                            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Eye size={18} className="text-white" />
                            </div>
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-green-600 font-semibold mb-1">✓ Uploaded</p>
                            <p className="text-[10px] font-mono text-muted-foreground truncate">
                              {form.speakerImageUrl}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                set('speakerImageUrl', '');
                                speakerImageInputRef.current?.click();
                              }}
                              className="text-xs text-primary hover:underline mt-1"
                            >
                              Replace
                            </button>
                          </div>
                        </div>
                      )}
                      {speakerImageUploadError && (
                        <p className="text-xs text-destructive mt-1.5">
                          ⚠ {speakerImageUploadError}
                        </p>
                      )}
                    </div>
                  )}

                  {speakerImageMode === 'url' && (
                    <div className="space-y-2">
                      <div className="relative">
                        <Link2
                          size={14}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          type="url"
                          className={inp() + ' pl-8'}
                          value={form.speakerImageUrl ?? ''}
                          onChange={(e) => set('speakerImageUrl', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                      {form.speakerImageUrl && getUrlWarning(form.speakerImageUrl) && (
                        <p className="text-xs text-amber-600 font-medium flex items-center gap-1 mt-1">
                          ⚠ {getUrlWarning(form.speakerImageUrl)}
                        </p>
                      )}
                      {form.speakerImageUrl && !getUrlWarning(form.speakerImageUrl) && (
                        <button
                          type="button"
                          onClick={() => setSpeakerImagePreview(true)}
                          className="relative group"
                          title="Click to preview"
                        >
                          <img
                            src={form.speakerImageUrl}
                            alt="Speaker preview"
                            className="h-20 w-20 rounded-full object-cover border border-border"
                            onError={(e) => (e.currentTarget.parentElement!.style.display = 'none')}
                            onLoad={(e) => (e.currentTarget.parentElement!.style.display = 'block')}
                          />
                          <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Eye size={18} className="text-white" />
                          </div>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </Field>
            </div>
              </div>
            )}
          </div>

          {/* 3. Video & Thumbnail */}
          <div className="bg-card border border-border shadow-sm rounded-xl overflow-hidden transition-all duration-200">
            {renderSectionHeader('media', <Video size={18} />, 'Video & Thumbnail')}
            
            {expandedSections['media'] && (
              <div className="px-5 pb-5 pt-3 space-y-4 border-t border-border/40 animate-in slide-in-from-top-2 fade-in duration-200">

            {/* YouTube URL */}
            {/* <Field label="YouTube URL" hint="optional">
              <div className="relative">
                <Video
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="url"
                  className={inp() + ' pl-8'}
                  value={form.youtubeUrl ?? ''}
                  onChange={(e) => set('youtubeUrl', e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
            </Field> */}

            {/* Thumbnail — Upload / URL toggle */}
            <Field label="Thumbnail Image" hint="optional">
              <div className="space-y-3">
                {/* Mode toggle */}
                <div className="flex items-center bg-muted rounded-lg p-0.5 w-fit text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setThumbnailMode('file');
                      setThumbnailUploadError('');
                    }}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all ${
                      thumbnailMode === 'file'
                        ? 'bg-background text-foreground shadow-sm font-semibold'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Upload size={11} /> Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setThumbnailMode('url');
                      setThumbnailUploadError('');
                    }}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all ${
                      thumbnailMode === 'url'
                        ? 'bg-background text-foreground shadow-sm font-semibold'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Globe size={11} /> Enter URL
                  </button>
                </div>

                {/* Hidden file input */}
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleThumbnailFileChange(e.target.files?.[0] ?? null)}
                />

                {/* Upload mode */}
                {thumbnailMode === 'file' && (
                  <div>
                    {!form.thumbnailUrl ? (
                      <button
                        type="button"
                        disabled={thumbnailUploading}
                        onClick={() => thumbnailInputRef.current?.click()}
                        className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border hover:border-primary/50 rounded-xl py-6 text-muted-foreground hover:text-primary transition-colors disabled:opacity-60"
                      >
                        {thumbnailUploading ? (
                          <>
                            <Loader2 size={22} className="animate-spin" />
                            <span className="text-xs">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload size={22} />
                            <span className="text-sm font-medium">Click to choose thumbnail</span>
                            <span className="text-xs">PNG, JPG, WEBP, GIF</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => setThumbnailPreview(true)}
                          className="relative group shrink-0"
                          title="Click to preview"
                        >
                          <img
                            src={form.thumbnailUrl}
                            alt="Thumbnail"
                            className="h-20 w-32 rounded-lg object-cover border border-border"
                          />
                          <div className="absolute inset-0 rounded-lg bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Eye size={18} className="text-white" />
                          </div>
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-green-600 font-semibold mb-1">✓ Uploaded</p>
                          <p className="text-[10px] font-mono text-muted-foreground truncate">
                            {form.thumbnailUrl}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              set('thumbnailUrl', '');
                              thumbnailInputRef.current?.click();
                            }}
                            className="text-xs text-primary hover:underline mt-1"
                          >
                            Replace
                          </button>
                        </div>
                      </div>
                    )}
                    {thumbnailUploadError && (
                      <p className="text-xs text-destructive mt-1.5">⚠ {thumbnailUploadError}</p>
                    )}
                  </div>
                )}

                {/* URL mode */}
                {thumbnailMode === 'url' && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Link2
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="url"
                        className={inp() + ' pl-8'}
                        value={form.thumbnailUrl ?? ''}
                        onChange={(e) => set('thumbnailUrl', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                    {form.thumbnailUrl && getUrlWarning(form.thumbnailUrl) && (
                      <p className="text-xs text-amber-600 font-medium flex items-center gap-1 mt-1">
                        ⚠ {getUrlWarning(form.thumbnailUrl)}
                      </p>
                    )}
                    {form.thumbnailUrl && !getUrlWarning(form.thumbnailUrl) && (
                      <button
                        type="button"
                        onClick={() => setThumbnailPreview(true)}
                        className="relative group"
                        title="Click to preview"
                      >
                        <img
                          src={form.thumbnailUrl}
                          alt="Thumbnail preview"
                          className="h-24 rounded-lg object-cover border border-border"
                          onError={(e) => (e.currentTarget.parentElement!.style.display = 'none')}
                          onLoad={(e) => (e.currentTarget.parentElement!.style.display = 'block')}
                        />
                        <div className="absolute inset-0 rounded-lg bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Eye size={18} className="text-white" />
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Field>
            </div>
            )}
          </div>

          {/* 4. Content Blocks */}
          <div className="bg-card border border-border shadow-sm rounded-xl overflow-hidden transition-all duration-200">
            {renderSectionHeader('content', <AlignLeft size={18} />, 'Session Content Blocks')}
            
            {expandedSections['content'] && (
              <div className="px-5 pb-5 pt-3 space-y-4 border-t border-border/40 animate-in slide-in-from-top-2 fade-in duration-200">
            <p className="text-xs text-muted-foreground -mt-2 mb-4">
              Build your session dynamically. Add text, galleries, or lists. Optional titles appear
              in the side navigation.
            </p>
            <ContentBlocksEditor
              blocks={form.contentBlocks}
              onChange={(blocks) => set('contentBlocks', blocks)}
            />
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT COLUMN — Sticky actions panel ── */}
        <div className="xl:sticky xl:top-[4.5rem] space-y-4">
          {/* Publish card */}
          <div className="bg-card border border-border shadow-sm rounded-xl p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-base text-foreground tracking-tight">Publish Settings</h3>
                <div className="mt-0.5 h-4">
                  {autoSaveStatus === 'saving' && (
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Auto-saving...
                    </span>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <span className="text-[11px] text-green-600 flex items-center gap-1">✓ Draft saved</span>
                  )}
                  {autoSaveStatus === 'error' && (
                    <span className="text-[11px] text-destructive flex items-center gap-1">⚠ Auto-save failed</span>
                  )}
                </div>
              </div>
              <Badge variant={form.status === 'Published' ? 'default' : 'warning'} className="rounded-md px-2.5 py-0.5 text-xs font-semibold">
                {form.status}
              </Badge>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                Visibility Status
              </label>
              <select
                className="w-full border border-border bg-background hover:border-primary/50 rounded-lg px-3.5 py-2 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm appearance-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.85rem center' }}
                value={form.status}
                onChange={(e) => set('status', e.target.value as 'Draft' | 'Published')}
              >
                <option value="Draft">📝 Draft — Hidden from portal</option>
                <option value="Published">🌐 Published — Visible to all</option>
              </select>
            </div>

            <div className="pt-4 space-y-2.5 border-t border-border/60">
              <Button
                type="button"
                className="w-full rounded-lg h-10 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                disabled={isSubmitting}
                onClick={() => handleSubmit(form.status as 'Draft' | 'Published')}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving changes...
                  </>
                ) : (
                  submitLabel
                )}
              </Button>
              <div className="grid grid-cols-2 gap-2.5">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-lg h-9 text-xs font-medium hover:border-primary/50 transition-colors"
                  onClick={() => setIsPreviewOpen(true)}
                >
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-lg h-9 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                  disabled={isSubmitting}
                  onClick={() => navigate('/sessions')}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>

          {/* Quick preview card */}
          <div className="bg-card border border-border shadow-sm rounded-xl p-5 space-y-3.5">
            <h3 className="font-semibold text-base text-foreground tracking-tight">Quick Preview</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2.5 text-foreground">
                <div className="p-1.5 bg-muted rounded-md text-muted-foreground"><Tag size={13} /></div>
                <span className={form.tag ? 'font-medium' : 'text-muted-foreground italic text-xs'}>
                  {form.tag || 'No tag set'}
                </span>
              </div>
              <div className="flex items-center gap-2.5 text-foreground">
                <div className="p-1.5 bg-muted rounded-md text-muted-foreground"><Mic2 size={13} /></div>
                <span className={form.speaker ? 'font-medium' : 'text-muted-foreground italic text-xs'}>
                  {form.speaker || 'No speaker set'}
                </span>
              </div>
              <div className="flex items-center gap-2.5 text-foreground">
                <div className="p-1.5 bg-muted rounded-md text-muted-foreground"><Clock size={13} /></div>
                <span className={form.duration ? 'font-medium' : 'text-muted-foreground italic text-xs'}>
                  {form.duration || 'No duration'} {form.date ? `· ${form.date}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-2.5 text-foreground">
                <div className="p-1.5 bg-muted rounded-md text-muted-foreground"><CheckSquare size={13} /></div>
                <span className="font-medium text-xs">
                  {form.contentBlocks?.length || 0} content blocks
                </span>
              </div>
            </div>

            {form.slug && (
              <div className="pt-3 border-t border-border/60">
                <p className="text-[11px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Portal URL</p>
                <div className="bg-muted/50 rounded-lg p-2.5">
                  <p className="text-xs font-mono text-primary break-all">/sessions/{form.slug}</p>
                </div>
              </div>
            )}
          </div>

          {/* Error summary if any */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 shadow-sm">
              <p className="text-sm font-semibold text-destructive mb-3 flex items-center gap-2">
                <AlertTriangle size={16} /> Please fix these fields:
              </p>
              <ul className="space-y-1.5">
                {Object.keys(errors).map((k) => (
                  <li key={k} className="text-xs text-destructive flex items-start gap-2">
                    <span className="mt-0.5 font-bold">•</span>
                    <span>
                      <span className="font-semibold capitalize">{k}</span>: {errors[k]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {isPreviewOpen && (
        <AdminSessionPreviewModal form={form} onClose={() => setIsPreviewOpen(false)} />
      )}
    </>
  );
}
